import jq from 'jquery';
import { cookieBase, notifyServer, userJobs } from 'server-data/flconfigs';
import { oneLine } from 'common-tags';
import _ from 'underscore';
import SockJS from 'sockjs';
import 'prism';
import 'prism/components/prism-php.js';
import 'prism/components/prism-ruby.js';
import 'prism/components/prism-python.js';
import 'prism/components/prism-java.js';
import 'prism/components/prism-clike.js';
import 'prism/components/prism-c.js';
import 'prism/components/prism-csharp.js';
import 'prism/components/prism-cpp.js';
import 'prism/components/prism-bash.js';
import 'prism/themes/prism-okaidia.css';

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || value !== value && other !== other;
}

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used by `_.defaults` to customize its `_.assignIn` use.
 *
 * @private
 * @param {*} objValue The destination value.
 * @param {*} srcValue The source value.
 * @param {string} key The key of the property to assign.
 * @param {Object} object The parent object of `objValue`.
 * @returns {*} Returns the value to assign.
 */
function assignInDefaults(objValue, srcValue, key, object) {
  if (objValue === undefined || eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key)) {
    return srcValue;
  }
  return objValue;
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$1.call(object, key) && eq(objValue, value)) || value === undefined && !(key in object)) {
    object[key] = value;
  }
}

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer ? customizer(object[key], source[key], key, object, source) : undefined;

    assignValue(object, key, newValue === undefined ? source[key] : newValue);
  }
  return object;
}

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0:
      return func.call(thisArg);
    case 1:
      return func.call(thisArg, args[0]);
    case 2:
      return func.call(thisArg, args[0], args[1]);
    case 3:
      return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  start = nativeMax(start === undefined ? func.length - 1 : start, 0);
  return function () {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = array;
    return apply(func, this, otherArgs);
  };
}

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function (object) {
    return object == null ? undefined : object[key];
  };
}

/**
 * Gets the "length" property value of `object`.
 *
 * **Note:** This function is used to avoid a
 * [JIT bug](https://bugs.webkit.org/show_bug.cgi?id=142792) that affects
 * Safari on at least iOS 8.1-8.3 ARM64.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {*} Returns the "length" value.
 */
var getLength = baseProperty('length');

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/6.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

/** `Object#toString` result references. */
var funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]';

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto$2.toString;

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 8 which returns 'object' for typed array and weak map constructors,
  // and PhantomJS 1.9 which returns 'function' for `NodeList` instances.
  var tag = isObject(value) ? objectToString.call(value) : '';
  return tag == funcTag || tag == genTag;
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This function is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/6.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length,
 *  else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(getLength(value)) && !isFunction(value);
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  length = length == null ? MAX_SAFE_INTEGER$1 : length;
  return !!length && (typeof value == 'number' || reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number' ? isArrayLike(object) && isIndex(index, object.length) : type == 'string' && index in object) {
    return eq(object[index], value);
  }
  return false;
}

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return baseRest(function (object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = assigner.length > 3 && typeof customizer == 'function' ? (length--, customizer) : undefined;

    if (guard && isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Built-in value references. */
var Reflect = root.Reflect;

/**
 * Converts `iterator` to an array.
 *
 * @private
 * @param {Object} iterator The iterator to convert.
 * @returns {Array} Returns the converted array.
 */
function iteratorToArray(iterator) {
  var data,
      result = [];

  while (!(data = iterator.next()).done) {
    result.push(data.value);
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Built-in value references. */
var enumerate = Reflect ? Reflect.enumerate : undefined,
    propertyIsEnumerable = objectProto$3.propertyIsEnumerable;

/**
 * The base implementation of `_.keysIn` which doesn't skip the constructor
 * property of prototypes or treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  object = object == null ? object : Object(object);

  var result = [];
  for (var key in object) {
    result.push(key);
  }
  return result;
}

// Fallback for IE < 9 with es6-shim.
if (enumerate && !propertyIsEnumerable.call({ 'valueOf': 1 }, 'valueOf')) {
  baseKeysIn = function baseKeysIn(object) {
    return iteratorToArray(enumerate(object));
  };
}

var baseKeysIn$1 = baseKeysIn;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike(value) && isArrayLike(value);
}

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$4.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$1 = objectProto$4.toString;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$4.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
function isArguments(value) {
  // Safari 8.1 incorrectly makes `arguments.callee` enumerable in strict mode.
  return isArrayLikeObject(value) && hasOwnProperty$2.call(value, 'callee') && (!propertyIsEnumerable$1.call(value, 'callee') || objectToString$1.call(value) == argsTag);
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

/** `Object#toString` result references. */
var stringTag = '[object String]';

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$2 = objectProto$5.toString;

/**
 * Checks if `value` is classified as a `String` primitive or object.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a string, else `false`.
 * @example
 *
 * _.isString('abc');
 * // => true
 *
 * _.isString(1);
 * // => false
 */
function isString(value) {
  return typeof value == 'string' || !isArray(value) && isObjectLike(value) && objectToString$2.call(value) == stringTag;
}

/**
 * Creates an array of index keys for `object` values of arrays,
 * `arguments` objects, and strings, otherwise `null` is returned.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array|null} Returns index keys, else `null`.
 */
function indexKeys(object) {
  var length = object ? object.length : undefined;
  if (isLength(length) && (isArray(object) || isString(object) || isArguments(object))) {
    return baseTimes(length, String);
  }
  return null;
}

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$6;

  return value === proto;
}

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  var index = -1,
      isProto = isPrototype(object),
      props = baseKeysIn$1(object),
      propsLength = props.length,
      indexes = indexKeys(object),
      skipIndexes = !!indexes,
      result = indexes || [],
      length = result.length;

  while (++index < propsLength) {
    var key = props[index];
    if (!(skipIndexes && (key == 'length' || isIndex(key, length))) && !(key == 'constructor' && (isProto || !hasOwnProperty$3.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * This method is like `_.assignIn` except that it accepts `customizer`
 * which is invoked to produce the assigned values. If `customizer` returns
 * `undefined`, assignment is handled by the method instead. The `customizer`
 * is invoked with five arguments: (objValue, srcValue, key, object, source).
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @alias extendWith
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} sources The source objects.
 * @param {Function} [customizer] The function to customize assigned values.
 * @returns {Object} Returns `object`.
 * @see _.assignWith
 * @example
 *
 * function customizer(objValue, srcValue) {
 *   return _.isUndefined(objValue) ? srcValue : objValue;
 * }
 *
 * var defaults = _.partialRight(_.assignInWith, customizer);
 *
 * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
 * // => { 'a': 1, 'b': 2 }
 */
var assignInWith = createAssigner(function (object, source, srcIndex, customizer) {
  copyObject(source, keysIn(source), object, customizer);
});

/** `Object#toString` result references. */
var errorTag = '[object Error]';

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$3 = objectProto$8.toString;

/**
 * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
 * `SyntaxError`, `TypeError`, or `URIError` object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an error object,
 *  else `false`.
 * @example
 *
 * _.isError(new Error);
 * // => true
 *
 * _.isError(Error);
 * // => false
 */
function isError(value) {
  if (!isObjectLike(value)) {
    return false;
  }
  return objectToString$3.call(value) == errorTag || typeof value.message == 'string' && typeof value.name == 'string';
}

/**
 * Attempts to invoke `func`, returning either the result or the caught error
 * object. Any additional arguments are provided to `func` when it's invoked.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Util
 * @param {Function} func The function to attempt.
 * @param {...*} [args] The arguments to invoke `func` with.
 * @returns {*} Returns the `func` result or error object.
 * @example
 *
 * // Avoid throwing errors for invalid selectors.
 * var elements = _.attempt(function(selector) {
 *   return document.querySelectorAll(selector);
 * }, '>_>');
 *
 * if (_.isError(elements)) {
 *   elements = [];
 * }
 */
var attempt = baseRest(function (func, args) {
  try {
    return apply(func, undefined, args);
  } catch (e) {
    return isError(e) ? e : new Error(e);
  }
});

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array ? array.length : 0,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

/**
 * The base implementation of `_.values` and `_.valuesIn` which creates an
 * array of `object` property values corresponding to the property names
 * of `props`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} props The property names to get values for.
 * @returns {Object} Returns the array of property values.
 */
function baseValues(object, props) {
  return arrayMap(props, function (key) {
    return object[key];
  });
}

/** Used to escape characters for inclusion in compiled string literals. */
var stringEscapes = {
  '\\': '\\',
  "'": "'",
  '\n': 'n',
  '\r': 'r',
  '\u2028': 'u2028',
  '\u2029': 'u2029'
};

/**
 * Used by `_.template` to escape characters for inclusion in compiled string literals.
 *
 * @private
 * @param {string} chr The matched character to escape.
 * @returns {string} Returns the escaped character.
 */
function escapeStringChar(chr) {
  return '\\' + stringEscapes[chr];
}

/**
 * Creates a function that invokes `func` with its first argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetPrototype = Object.getPrototypeOf;

/**
 * Gets the `[[Prototype]]` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {null|Object} Returns the `[[Prototype]]`.
 */
var getPrototype = overArg(nativeGetPrototype, Object);

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.has` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHas(object, key) {
  // Avoid a bug in IE 10-11 where objects with a [[Prototype]] of `null`,
  // that are composed entirely of index properties, return `false` for
  // `hasOwnProperty` checks of them.
  return object != null && (hasOwnProperty$4.call(object, key) || typeof object == 'object' && key in object && getPrototype(object) === null);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = Object.keys;

/**
 * The base implementation of `_.keys` which doesn't skip the constructor
 * property of prototypes or treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
var baseKeys = overArg(nativeKeys, Object);

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/6.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  var isProto = isPrototype(object);
  if (!(isProto || isArrayLike(object))) {
    return baseKeys(object);
  }
  var indexes = indexKeys(object),
      skipIndexes = !!indexes,
      result = indexes || [],
      length = result.length;

  for (var key in object) {
    if (baseHas(object, key) && !(skipIndexes && (key == 'length' || isIndex(key, length))) && !(isProto && key == 'constructor')) {
      result.push(key);
    }
  }
  return result;
}

/** Used to match template delimiters. */
var reInterpolate = /<%=([\s\S]+?)%>/g;

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyOf(object) {
  return function (key) {
    return object == null ? undefined : object[key];
  };
}

/** Used to map characters to HTML entities. */
var htmlEscapes = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;'
};

/**
 * Used by `_.escape` to convert characters to HTML entities.
 *
 * @private
 * @param {string} chr The matched character to escape.
 * @returns {string} Returns the escaped character.
 */
var escapeHtmlChar = basePropertyOf(htmlEscapes);

/** Built-in value references. */
var Symbol = root.Symbol;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/** Used for built-in method references. */
var objectProto$10 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$4 = objectProto$10.toString;

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' || isObjectLike(value) && objectToString$4.call(value) == symbolTag;
}

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = value + '';
  return result == '0' && 1 / value == -INFINITY ? '-0' : result;
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/** Used to match HTML entities and HTML characters. */
var reUnescapedHtml = /[&<>"'`]/g,
    reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

/**
 * Converts the characters "&", "<", ">", '"', "'", and "\`" in `string` to
 * their corresponding HTML entities.
 *
 * **Note:** No other characters are escaped. To escape additional
 * characters use a third-party library like [_he_](https://mths.be/he).
 *
 * Though the ">" character is escaped for symmetry, characters like
 * ">" and "/" don't need escaping in HTML and have no special meaning
 * unless they're part of a tag or unquoted attribute value. See
 * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
 * (under "semi-related fun fact") for more details.
 *
 * Backticks are escaped because in IE < 9, they can break out of
 * attribute values or HTML comments. See [#59](https://html5sec.org/#59),
 * [#102](https://html5sec.org/#102), [#108](https://html5sec.org/#108), and
 * [#133](https://html5sec.org/#133) of the
 * [HTML5 Security Cheatsheet](https://html5sec.org/) for more details.
 *
 * When working with HTML you should always
 * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
 * XSS vectors.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category String
 * @param {string} [string=''] The string to escape.
 * @returns {string} Returns the escaped string.
 * @example
 *
 * _.escape('fred, barney, & pebbles');
 * // => 'fred, barney, &amp; pebbles'
 */
function escape$1(string) {
    string = toString(string);
    return string && reHasUnescapedHtml.test(string) ? string.replace(reUnescapedHtml, escapeHtmlChar) : string;
}

/** Used to match template delimiters. */
var reEscape = /<%-([\s\S]+?)%>/g;

/** Used to match template delimiters. */
var reEvaluate = /<%([\s\S]+?)%>/g;

/**
 * By default, the template delimiters used by lodash are like those in
 * embedded Ruby (ERB). Change the following template settings to use
 * alternative delimiters.
 *
 * @static
 * @memberOf _
 * @type {Object}
 */
var templateSettings = {

  /**
   * Used to detect `data` property values to be HTML-escaped.
   *
   * @memberOf _.templateSettings
   * @type {RegExp}
   */
  'escape': reEscape,

  /**
   * Used to detect code to be evaluated.
   *
   * @memberOf _.templateSettings
   * @type {RegExp}
   */
  'evaluate': reEvaluate,

  /**
   * Used to detect `data` property values to inject.
   *
   * @memberOf _.templateSettings
   * @type {RegExp}
   */
  'interpolate': reInterpolate,

  /**
   * Used to reference the data object in the template text.
   *
   * @memberOf _.templateSettings
   * @type {string}
   */
  'variable': '',

  /**
   * Used to import variables into the compiled template.
   *
   * @memberOf _.templateSettings
   * @type {Object}
   */
  'imports': {

    /**
     * A reference to the `lodash` function.
     *
     * @memberOf _.templateSettings.imports
     * @type {Function}
     */
    '_': { 'escape': escape$1 }
  }
};

/** Used to match empty string literals in compiled template source. */
var reEmptyStringLeading = /\b__p \+= '';/g,
    reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
    reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

/**
 * Used to match
 * [ES template delimiters](http://ecma-international.org/ecma-262/6.0/#sec-template-literal-lexical-components).
 */
var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

/** Used to ensure capturing order of template delimiters. */
var reNoMatch = /($^)/;

/** Used to match unescaped characters in compiled string literals. */
var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

/**
 * Creates a compiled template function that can interpolate data properties
 * in "interpolate" delimiters, HTML-escape interpolated data properties in
 * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
 * properties may be accessed as free variables in the template. If a setting
 * object is given, it takes precedence over `_.templateSettings` values.
 *
 * **Note:** In the development build `_.template` utilizes
 * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
 * for easier debugging.
 *
 * For more information on precompiling templates see
 * [lodash's custom builds documentation](https://lodash.com/custom-builds).
 *
 * For more information on Chrome extension sandboxes see
 * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category String
 * @param {string} [string=''] The template string.
 * @param {Object} [options={}] The options object.
 * @param {RegExp} [options.escape=_.templateSettings.escape]
 *  The HTML "escape" delimiter.
 * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
 *  The "evaluate" delimiter.
 * @param {Object} [options.imports=_.templateSettings.imports]
 *  An object to import into the template as free variables.
 * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
 *  The "interpolate" delimiter.
 * @param {string} [options.sourceURL='templateSources[n]']
 *  The sourceURL of the compiled template.
 * @param {string} [options.variable='obj']
 *  The data object variable name.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Function} Returns the compiled template function.
 * @example
 *
 * // Use the "interpolate" delimiter to create a compiled template.
 * var compiled = _.template('hello <%= user %>!');
 * compiled({ 'user': 'fred' });
 * // => 'hello fred!'
 *
 * // Use the HTML "escape" delimiter to escape data property values.
 * var compiled = _.template('<b><%- value %></b>');
 * compiled({ 'value': '<script>' });
 * // => '<b>&lt;script&gt;</b>'
 *
 * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
 * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
 * compiled({ 'users': ['fred', 'barney'] });
 * // => '<li>fred</li><li>barney</li>'
 *
 * // Use the internal `print` function in "evaluate" delimiters.
 * var compiled = _.template('<% print("hello " + user); %>!');
 * compiled({ 'user': 'barney' });
 * // => 'hello barney!'
 *
 * // Use the ES delimiter as an alternative to the default "interpolate" delimiter.
 * var compiled = _.template('hello ${ user }!');
 * compiled({ 'user': 'pebbles' });
 * // => 'hello pebbles!'
 *
 * // Use backslashes to treat delimiters as plain text.
 * var compiled = _.template('<%= "\\<%- value %\\>" %>');
 * compiled({ 'value': 'ignored' });
 * // => '<%- value %>'
 *
 * // Use the `imports` option to import `jQuery` as `jq`.
 * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
 * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
 * compiled({ 'users': ['fred', 'barney'] });
 * // => '<li>fred</li><li>barney</li>'
 *
 * // Use the `sourceURL` option to specify a custom sourceURL for the template.
 * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
 * compiled(data);
 * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
 *
 * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
 * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
 * compiled.source;
 * // => function(data) {
 * //   var __t, __p = '';
 * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
 * //   return __p;
 * // }
 *
 * // Use custom template delimiters.
 * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
 * var compiled = _.template('hello {{ user }}!');
 * compiled({ 'user': 'mustache' });
 * // => 'hello mustache!'
 *
 * // Use the `source` property to inline compiled templates for meaningful
 * // line numbers in error messages and stack traces.
 * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
 *   var JST = {\
 *     "main": ' + _.template(mainText).source + '\
 *   };\
 * ');
 */
function template(string, options, guard) {
  // Based on John Resig's `tmpl` implementation
  // (http://ejohn.org/blog/javascript-micro-templating/)
  // and Laura Doktorova's doT.js (https://github.com/olado/doT).
  var settings = templateSettings.imports._.templateSettings || templateSettings;

  if (guard && isIterateeCall(string, options, guard)) {
    options = undefined;
  }
  string = toString(string);
  options = assignInWith({}, options, settings, assignInDefaults);

  var imports = assignInWith({}, options.imports, settings.imports, assignInDefaults),
      importsKeys = keys(imports),
      importsValues = baseValues(imports, importsKeys);

  var isEscaping,
      isEvaluating,
      index = 0,
      interpolate = options.interpolate || reNoMatch,
      source = "__p += '";

  // Compile the regexp to match each delimiter.
  var reDelimiters = RegExp((options.escape || reNoMatch).source + '|' + interpolate.source + '|' + (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' + (options.evaluate || reNoMatch).source + '|$', 'g');

  // Use a sourceURL for easier debugging.
  var sourceURL = 'sourceURL' in options ? '//# sourceURL=' + options.sourceURL + '\n' : '';

  string.replace(reDelimiters, function (match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
    interpolateValue || (interpolateValue = esTemplateValue);

    // Escape characters that can't be included in string literals.
    source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

    // Replace delimiters with snippets.
    if (escapeValue) {
      isEscaping = true;
      source += "' +\n__e(" + escapeValue + ") +\n'";
    }
    if (evaluateValue) {
      isEvaluating = true;
      source += "';\n" + evaluateValue + ";\n__p += '";
    }
    if (interpolateValue) {
      source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
    }
    index = offset + match.length;

    // The JS engine embedded in Adobe products needs `match` returned in
    // order to produce the correct `offset` value.
    return match;
  });

  source += "';\n";

  // If `variable` is not specified wrap a with-statement around the generated
  // code to add the data object to the top of the scope chain.
  var variable = options.variable;
  if (!variable) {
    source = 'with (obj) {\n' + source + '\n}\n';
  }
  // Cleanup code by stripping empty strings.
  source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source).replace(reEmptyStringMiddle, '$1').replace(reEmptyStringTrailing, '$1;');

  // Frame code as the function body.
  source = 'function(' + (variable || 'obj') + ') {\n' + (variable ? '' : 'obj || (obj = {});\n') + "var __t, __p = ''" + (isEscaping ? ', __e = _.escape' : '') + (isEvaluating ? ', __j = Array.prototype.join;\n' + "function print() { __p += __j.call(arguments, '') }\n" : ';\n') + source + 'return __p\n}';

  var result = attempt(function () {
    return Function(importsKeys, sourceURL + 'return ' + source).apply(undefined, importsValues);
  });

  // Provide the compiled function's source by its `toString` method or
  // the `source` property as a convenience for inlining compiled templates.
  result.source = source;
  if (isError(result)) {
    throw result;
  }
  return result;
}

/** `Object#toString` result references. */
var numberTag = '[object Number]';

/** Used for built-in method references. */
var objectProto$11 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$5 = objectProto$11.toString;

/**
 * Checks if `value` is classified as a `Number` primitive or object.
 *
 * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
 * classified as numbers, use the `_.isFinite` method.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a number, else `false`.
 * @example
 *
 * _.isNumber(3);
 * // => true
 *
 * _.isNumber(Number.MIN_VALUE);
 * // => true
 *
 * _.isNumber(Infinity);
 * // => true
 *
 * _.isNumber('3');
 * // => false
 */
function isNumber(value) {
  return typeof value == 'number' || isObjectLike(value) && objectToString$5.call(value) == numberTag;
}

function setCookie(cName, value, expiredays) {
  var expiremins = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;

  var exdate = new Date();
  exdate.setDate(exdate.getDate() + expiredays);
  exdate.setMinutes(exdate.getMinutes() + expiremins);
  document.cookie = cName + '=' + value + ('' + (expiredays == null ? '' : ';expires=' + exdate.toUTCString())) + '; path=/; secure';
}

function getCookie(cName) {
  var cookies = document.cookie;
  if (cookies.length > 0) {
    var cStart = cookies.indexOf(cName + '=');
    if (cStart != -1) {
      cStart = cStart + cName.length + 1;
      var cEnd = cookies.indexOf(';', cStart);
      if (cEnd == -1) cEnd = cookies.length;
      return cookies.substring(cStart, cEnd);
    }
  }
  return '';
}

function getUserIdfromCookie() {
  return getCookie(cookieBase + '_USER_ID');
}

function randomString(length, chars) {
  var result = '';
  for (var i = length; i > 0; --i) {
    result += chars[Math.round(Math.random() * (chars.length - 1))];
  }
  return result;
}

function getCSRFToken() {
  var token = getCookie('XSRF-TOKEN');
  if (!token) {
    token = randomString(64, '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ');
    setCookie('XSRF-TOKEN', token, 7);
  }
  return token;
}

var TRANSLATE_MODE_COOKIE = cookieBase + '_TRANSLATE_MODE';

var isTranslateModeEnabled = function isTranslateModeEnabled() {
  return getCookie(TRANSLATE_MODE_COOKIE) === '1';
};

var getTokenizedMessage = function getTokenizedMessage(id, message) {
  return isTranslateModeEnabled() ? '~=[' + id + '|^|' + message + ']=~' : message;
};

var translate = function translate(first) {
  for (var _len = arguments.length, rest = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    rest[_key - 1] = arguments[_key];
  }

  switch (true) {
    case isString(first):
      {
        var text = first;
        var vars = rest[0];
        if (typeof vars === 'undefined' || !vars) {
          return text;
        }
        var result = template(text, {
          evaluate: /<\[([\s\S]+?)\]>/g,
          interpolate: /<\[=([\s\S]+?)\]>/g,
          escape: /<\[-([\s\S]+?)\]>/g
        })(vars);
        result = template(result)(vars);
        return result;
      }
    case isArray(first):
      {
        return oneLine([translate.raw.apply(translate, [first].concat(rest))]);
      }
    case isNumber(first):
      {
        var _ret = function () {
          var sourceTextId = first;
          return {
            v: function v() {
              return getTokenizedMessage(sourceTextId, translate.apply(undefined, arguments));
            }
          };
        }();

        if (typeof _ret === "object") return _ret.v;
      }
    default:
      {
        throw new TypeError('Invalid argument types');
      }
  }
};

translate.raw = function rawT(literals) {
  for (var _len2 = arguments.length, placeholders = Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    placeholders[_key2 - 1] = arguments[_key2];
  }

  return literals.raw.reduce(function (str, current, index) {
    var temp = str + current;
    if (index < placeholders.length) {
      temp += placeholders[index].toString();
    }
    return temp;
  }, '');
};

function LibnotifyFactory() {
  var socket = void 0;
  var isReady = false;
  var readyList = [];
  var registeredFunctions = [];
  var registeredFilters = [];
  var maxSeenPackets = 5;
  var seenPackets = {
    last: undefined,
    first: undefined,
    packets: {}
  };
  var jobChannels = void 0;
  var eventHistory = [];

  var addPacketNoDelete = function addPacketNoDelete(id, packetId) {
    seenPackets.packets[id] = {
      id: packetId,
      next: undefined
    };
    seenPackets.packets[seenPackets.first].next = id;
    seenPackets.first = id;
  };

  var addPacket = function addPacket(id) {
    var last = seenPackets.last;
    var lastPacket = seenPackets.packets[last];
    var newLast = lastPacket.next;

    addPacketNoDelete(id, id);
    seenPackets.last = newLast;
    delete seenPackets.packets[last];
  };

  // Packet is only considered seen if it exists and if it's not a placeholder
  var newPacket = function newPacket(id) {
    return !(seenPackets.packets[id] && seenPackets.packets[id].id === id);
  };

  (function () {
    seenPackets.packets[0] = {
      id: false,
      next: undefined
    };
    seenPackets.first = 0;
    seenPackets.last = 0;

    for (var i = 1; i <= maxSeenPackets; i++) {
      addPacketNoDelete(i, false);
    }
  })();

  var messageReceived = function messageReceived(obj) {
    var key = obj.parent_type + ':' + obj.type;
    if (newPacket(obj.id)) {
      addPacket(obj.id);

      var allowed = true;
      jq(registeredFilters[key]).each(function (i, e) {
        if (e(obj) === false) {
          allowed = false;
          return false;
        }
      });

      if (!allowed) {
        return false;
      }

      jq(registeredFunctions[key]).each(function (i, e) {
        var objCopy = jq.extend(true, {}, obj);
        e(objCopy);
      });
    }
  };

  var register = function register(parentType, channel, callback) {
    var key = parentType + ':' + channel;
    if (registeredFunctions[key]) {
      var callbackExists = _.find(registeredFunctions[key], function (cb) {
        return cb == callback;
      });
      if (!callbackExists) {
        registeredFunctions[key].push(callback);
      }
    } else {
      registeredFunctions[key] = new Array(callback);
    }

    return this;
  };

  var registerFilter = function registerFilter(parentType, channel, callback) {
    var key = parentType + ':' + channel;
    if (registeredFilters[key]) {
      var callbackExists = _.find(registeredFilters[key], function (cb) {
        return cb == callback;
      });
      if (!callbackExists) {
        registeredFilters[key].push(callback);
      }
    } else {
      registeredFilters[key] = new Array(callback);
    }

    return this;
  };

  var registerHeartbeatCallback = function registerHeartbeatCallback(heartbeatCallback) {
    socket.onheartbeat = heartbeatCallback;
  };

  var unsubscribeChannel = function unsubscribeChannel(parentType, channel) {
    var key = parentType + ':' + channel;
    if (registeredFunctions[key]) {
      delete registeredFunctions[key];
    }

    return this;
  };

  var ready = function ready(f, args, context) {
    context = context || window;
    args = args || [];
    if (isReady) {
      f.apply(context, args);
    } else {
      readyList.push({
        f: f,
        args: args,
        context: context
      });
    }
  };

  var setReady = function setReady() {
    for (var i = 0; i < readyList.length; i++) {
      var obj = readyList[i];
      obj.f.apply(obj.context, obj.args);
    }
    readyList = [];
  };

  var send = function send(channel, data) {
    socket.send(JSON.stringify({
      channel: channel,
      body: data
    }));
  };

  var closeSocket = function closeSocket() {
    if (socket) {
      socket.close();
    }
  };

  var setJobChannel = function setJobChannel(channels) {
    if (typeof channels !== 'undefined') {
      if (typeof jobChannels === 'undefined' || jobChannels.join('') !== channels.join('')) {
        jobChannels = channels;
        closeSocket();
      }
    }
  };

  var getJobChannel = function getJobChannel() {
    if (typeof jobChannels === 'undefined') {
      jobChannels = userJobs;
    }
    return jobChannels;
  };

  var getSocketReadyState = function getSocketReadyState() {
    return socket && socket.readyState;
  };

  var Backoff = function Backoff(name, resetTime, firstTimeout) {
    this.name = name;

    // Is this the first time its failed
    this.first_fail = true;

    // How long is the wait for first failure
    this.first_timeout = firstTimeout;

    // The current failure wait time
    this.wait_time = resetTime;

    // The base failure wait time
    this.reset_time = resetTime;
  };

  Backoff.prototype.fail = function () {
    var delay = void 0;
    if (this.first_fail) {
      this.first_fail = false;
      delay = this.first_timeout;
    } else {
      delay = this.wait_time;
      this.wait_time *= 2;
      this.wait_time = Math.min(this.wait_time, 60000);
    }
    window.console.log(this.name + ' Failed retrying in ' + delay);
    return delay;
  };

  Backoff.prototype.ok = function () {
    var _this = this;

    if (!this.first_fail) {
      (function () {
        window.console.log(_this.name + ' Recovered');
        var event = void 0;
        var eventString = 'libnotify.connected';
        var eventImmediate = void 0;
        var eventImmediateString = 'libnotify.connected.immediate';
        try {
          event = new Event(eventString);
          eventImmediate = new Event(eventImmediateString);
        } catch (error) {
          event = document.createEvent('Event');
          eventImmediate = document.createEvent('Event');
          event.initEvent(eventString, true, false);
          eventImmediate.initEvent(eventImmediateString, true, false);
        }
        setTimeout(function () {
          document.dispatchEvent(event);
        }, Math.random() * 30000);
        document.dispatchEvent(eventImmediate);
      })();
    }
    this.first_fail = true;
    this.wait_time = this.reset_time;
  };

  var init = function init(authFailCallback) {
    var connectBackoff = new Backoff('sockJS Connect', 1000, 200);
    var reconnectBackoff = new Backoff('sockJS Reconnect', 1000, 200);
    var authBackoff = new Backoff('sockJS Auth', 4000, 500);

    var cookies = {
      hash: getCookie(cookieBase + '_AUTH_HASH'),
      hash2: getCookie(cookieBase + '_AUTH_HASH_V2'),
      user_id: getCookie(cookieBase + '_USER_ID'),
      channels: getJobChannel()
    };

    // Dont talk to node if not loged in
    if (cookies.hash2 == '' && cookies.hash == '' || cookies.user_id == '') {
      return false;
    }

    var newSocket = function newSocket() {
      socket = new SockJS(notifyServer);
      // Always get latest channels when socket reopens
      cookies.channels = getJobChannel();

      socket.onmessage = function (e) {
        var message = JSON.parse(e.data);
        if (eventHistory.length < 100 && (message.body.parent_type === 'onlineoffline' || message.channel === 'subscribe')) {
          eventHistory.push(message);
        }

        if (message.channel == 'user') {
          messageReceived(message.body);
        } else if (message.channel == 'subscribe') {
          reconnectBackoff.ok();
          if (message.body == 'NO') {
            authFailCallback();
            socket.close(4000, 'Auth Failure');
          } else {
            authBackoff.ok();
            isReady = true;
            setReady();
          }
        }
      };

      socket.onopen = function () {
        // This is a temporary hack to help track a bug with online offline...
        // Will fix in a week 20/07/17.
        var sessionId = void 0;
        if (socket && socket._transport && socket._transport.url && socket._transport.url.split('/').length !== 0) {
          sessionId = socket._transport.url.split('/')[4];
        }
        var sessionIdTimestamp = new Date().getTime();
        if (window.sessionStorage) {
          window.sessionStorage.setItem('notification_socket_session_id', sessionId);
          window.sessionStorage.setItem('notification_socket_session_time', sessionIdTimestamp);
        }
        connectBackoff.ok();
        socket.send(JSON.stringify({
          channel: 'auth',
          body: cookies
        }));
      };

      socket.onclose = function (e) {
        socket = undefined;
        isReady = false;
        if (e.code === 4000) {
          setTimeout(newSocket, authBackoff.fail());
        } else if (!e.wasClean) {
          setTimeout(newSocket, connectBackoff.fail());
        } else {
          setTimeout(newSocket, reconnectBackoff.fail());
        }
      };
    };
    newSocket();
  };

  var getEventHistory = function getEventHistory() {
    return eventHistory;
  };

  init(function () {
    // console.log('auth failure');
  });

  // public interface
  return {
    log: true,
    register: register,
    register_filter: registerFilter,
    registerHeartbeatCallback: registerHeartbeatCallback,
    unsubscribeChannel: unsubscribeChannel,
    close: closeSocket,
    packets: seenPackets,
    send: send,
    ready: ready,
    set_job_channel: setJobChannel,
    getSocketReadyState: getSocketReadyState,
    getEventHistory: getEventHistory
  };
}

window.libnotify = window.libnotify || LibnotifyFactory();
var libnotify = window.libnotify;

var watchedUsers = {};
var userCache = {};

var statusMap = {
  1: 'offline',
  2: 'online'
};

var sendWithLibnotify = function sendWithLibnotify(command, userList) {
  libnotify.ready(function () {
    libnotify.send('onlineoffline', {
      route: command,
      users: userList
    });
  });
};

var splitNewUsers = function splitNewUsers(userList) {
  var newUsers = [];
  var existing = [];

  if (_.isArray(userList)) {
    for (var i = 0; i < userList.length; i++) {
      if (userList[i] in watchedUsers) {
        existing.push(parseInt(userList[i], 10));
      } else {
        newUsers.push(parseInt(userList[i], 10));
      }
    }
  } else if (userList) {
    Object.keys(userList).forEach(function (user) {
      if (user in watchedUsers) {
        existing.push(parseInt(user, 10));
      } else {
        newUsers.push(parseInt(user, 10));
      }
    });
  }

  return {
    new_users: newUsers,
    existing: existing
  };
};

var addToWatched = function addToWatched(userList) {
  for (var i = 0; i < userList.length; i++) {
    watchedUsers[userList[i]] = true;
  }
};

// CSS functions
var styleIdPrefix = 'onlineoffline_style_';

var stateColours = {
  online: '#77be56 !important',
  offline: '#aaaaaa !important'
};

var styleTemplate = _.template('.online-status.onlineoffline_<%= user %> {background: <%= colour %>;}\n' + "<% if (state != 'online') { %>" + '.onlineoffline.online_<%= user %>{display: none !important;}<% } %>\n' + '.onlineoffline.online_<%= user %>{display: ' + "<% if (state != 'online') { %>none !important<% } " + 'else { %> block <% } %>;}\n.onlineoffline.offline_<%= user %>{' + "display: <% if (state != 'offline') { %>none !important<% } " + 'else { %> block <% } %>;}\n');

var destroyStyle = function destroyStyle(userId) {
  jq('#' + styleIdPrefix + userId).remove();
};

var setStyle = function setStyle(userId, state) {
  var styleText = styleTemplate({
    user: userId,
    colour: stateColours[state],
    state: state
  });
  var $style = jq('<style type=\'text/css\' id=\'' + styleIdPrefix + userId + '\'>' + (styleText + '</style>'));

  destroyStyle(userId);
  jq('head').append($style);
};

var setStatus = function setStatus(userId, stateId) {
  userCache[userId] = stateId;

  var state = statusMap[stateId] || 'offline';

  setStyle(userId, state);
};

// Public Functions
var getSync = function getSync(userId) {
  return statusMap[userCache[userId]] || 'offline';
};

var sub = function sub(userList) {
  var res = splitNewUsers(userList);
  addToWatched(res.new_users);

  if (res.new_users.length) {
    sendWithLibnotify('getsub', res.new_users);
  }
};

// Initilisation code
libnotify.register('onlineoffline', 'statuschange', function (msg) {
  setStatus(msg.data.user, msg.data.to);
});

libnotify.register('onlineoffline', 'statusget', function (msg) {
  if (msg.data) {
    Object.keys(msg.data).forEach(function (user) {
      setStatus(user, msg.data[user]);
    });
  }
});

libnotify.register('onlineoffline', 'reregister', function () {
  setTimeout(function () {
    // console.log('Onlineoffline: Reregistering');
    var watched = [];
    if (userCache) {
      Object.keys(userCache).forEach(function (user) {
        watched.push(user);
      });
    }
    sendWithLibnotify('getsub', watched);
  }, 500);
});

var OnlineOffline = {
  set_status: setStatus,
  get: getSync,
  sub: sub,
  map: statusMap
};

/*
  QTS Binding Library
  This includes functions for binding qts pushing to events, as follows:
  init()
  - initializes default values for all qts events
    this may set two separate default values for the navbar and for the page
    push()
  - used for js process events, not directly w/ user interaction
    bind()
  - bind to an element click event.
    if contains a link, it shall prevent & setTimeout to allow qts pixel
    request, before redirecting.
*/

// general container for all qtsbinds data;
var qtsv = {
  timeout: false,
  defaults: {},
  page: {},
  nav: {}
};

qtsv.defaults = {
  pageContainer: '.gaf-container:first',
  navContainer: '.header-and-nav'
};

var setDefault = function setDefault(params, isNav) {
  if (typeof isNav === 'boolean' && isNav) {
    if (params) {
      Object.keys(params).forEach(function (pkey) {
        qtsv.nav[pkey] = params[pkey];
      });
    }
  } else if (params) {
    Object.keys(params).forEach(function (pkey) {
      qtsv.page[pkey] = params[pkey];
    });
  }
  qtsv.timeout = false;
};

var push = function push(params, url, isNav) {
  var qvars = qtsv;
  if (!qvars.timeout) {
    (function () {
      var paramSet = typeof isNav === 'undefined' ? JSON.parse(JSON.stringify(qvars.page)) : JSON.parse(JSON.stringify(qvars.nav));

      if (typeof params === 'object') {
        if (params) {
          Object.keys(params).forEach(function (pkey) {
            if (pkey != 'eventName') {
              paramSet[pkey] = params[pkey];
            }
          });
        }
      } else if (typeof params === 'string') {
        paramSet.label = params;
      }

      if (typeof params.eventName !== 'undefined') {
        _t.push([params.eventName, paramSet]);
      } else {
        _t.push(['user_action', paramSet]);
      }

      setTimeout(function () {
        if (typeof url === 'string') {
          window.location = url;
        }
      }, 1000);

      setTimeout(function () {
        qvars.timeout = false;
      }, 100);

      qvars.timeout = true;
    })();
  }
};

var createBind = function createBind(ifselector, params, isNav) {
  var qvars = qtsv;

  params = params || {};

  var containerEl = qvars.defaults.pageContainer;
  var selector = '[data-qts]';

  if (typeof ifselector === 'string') {
    selector = ifselector;
  } else if (typeof ifselector === 'object') {
    selector = ifselector.id;
    // attach conditional values to element so it won't be lost after binding
    jq(selector).attr('data-qtcon', ifselector.con);
    jq(selector).attr('data-qt0', ifselector.falsy);
    jq(selector).attr('data-qt1', ifselector.truthy);
  }

  if (typeof isNav === 'string') {
    containerEl = isNav;
  } else if (typeof isNav === 'boolean' && isNav == true) {
    containerEl = qvars.defaults.navContainer;
  }

  jq(containerEl).on('click', selector, function (e) {
    e.preventDefault();
    // if navbar is a link, assign triggerable flag so it can be triggered after
    // qts is pushed.
    var paramSet = typeof isNav === 'undefined' ? JSON.parse(JSON.stringify(qvars.page)) : JSON.parse(JSON.stringify(qvars.nav));

    var thisEl = jq(e.currentTarget);
    var triggerable = false;
    if (typeof thisEl.attr('href') !== 'undefined') {
      triggerable = true;
    }

    if (!qtsv.timeout) {
      if (params) {
        Object.keys(params).forEach(function (pkey) {
          if (pkey != 'eventName') {
            paramSet[pkey] = params[pkey];
          }
        });
      }

      var label = thisEl.data('qts');

      if (typeof label !== 'undefined') {
        paramSet.label = label;
      }

      // for conditional selectors
      var hasCondition = typeof thisEl.data('qtcon') !== 'undefined';

      if (hasCondition) {
        // toggle text value (qt1 or qt0) depending on condition (qtcon)
        if (thisEl.data('qtcon') == false) {
          paramSet.label = thisEl.data('qt1');
          thisEl.data('qtcon', true);
        } else {
          paramSet.label = thisEl.data('qt0');
          thisEl.data('qtcon', false);
        }
      }

      if (typeof paramSet.action === 'undefined') {
        paramSet.action = 'click';
      }

      if (typeof params.eventName !== 'undefined') {
        _t.push([params.eventName, paramSet]);
      } else {
        _lt.push(['user_action', paramSet]);
      }

      // sets a timeout fxn & timer to measure if qts push taking too long.
      qtsv.timer = setTimeout(function () {
        qtsv.timeout = false;
        if (triggerable) {
          window.location = thisEl.attr('href');
        }
      }, 1000);

      qtsv.timeout = true;
      qtsv.timerRun = 0;
    }
  });
};

qtsv.push = push;

var qtsp = {
  init: setDefault,
  bind: createBind,
  push: push
};

function processEvent(event) {
  var eventName = event.event;
  if (eventName) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
  if (eventName == 'NonHireMe Posted') {
    _ttref.push(['_trackEvent', 'PostProject', 'Posted']);
    _ttref.push(['_clearEntrypoint', 'Project']);
    _ttref.push(['_setEntrypoint', 'Project_PostProjectForm']);
  } else if (eventName == 'Buy Exam') {
    _ttref.push(['_trackEvent', 'Exams', 'ConfirmBuy', event.examName]);
  } else if (eventName == 'Bid on Project') {
    _ttref.push(['_trackEvent', 'Project', 'Bid', '', 0, true, true]);
    qtsp.push({
      label: 'PlaceBid',
      amount: event.amount,
      currency: event.currency
    });
  } else if (eventName == 'Contest Reposted') {
    _ttref.push(['_setEntrypoint', 'Contest_RepostSuccessContest']);
  } else if (eventName == 'Entries Submitted') {
    // track the submitted entries from dropdown notification
    if (event.xp_contest) {
      _ttref.push(['_trackEvent', 'Contest', 'SubmitEntry', 'XP Contest']);
      _ttref.push(['_clearEntrypoint', 'Contest']);
    }
    if (event.realtime_contest) {
      _ttref.push(['_trackEvent', 'Contest', 'SubmitEntry', 'Realtime Notification Contest']);
      _ttref.push(['_clearEntrypoint', 'Contest']);
    }
  } else if (eventName == 'Suggestion Click') {
    _t.push(['project_suggestion_redirect', {
      bidder_id: event.bidder_id,
      src_project_id: event.src_project_id,
      dst_project_id: event.dst_project_id,
      time: new Date()
    }]);
    _ttref.push(['_clearEntrypoint', 'Project']);
    _ttref.push(['_setEntrypoint', 'Project_Suggestion']);
    _ttref.push(['_trackEvent', 'Project', 'Suggestion', 'clicked', 0, false, true]);
  } else if (eventName == 'Service View') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      label: 'ViewPage',
      action: 'view'
    }]);
  } else if (eventName == 'Service Order Modal Cancel') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderModal',
      label: 'OrderModalCancel',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Button Click') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      label: 'OrderButton',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Confirm Modal') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderModal',
      label: 'OrderModal',
      action: 'view'
    }]);
  } else if (eventName == 'Service Order Button Click Logged Out') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      label: 'OrderButtonLoggedOut',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Payment Verified') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      label: 'OrderModalOkPaymentVerified',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Modal Purchase') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      label: 'OrderModalOkPurchase',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Modal Ok') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderModal',
      label: 'OrderModalOk',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Login Modal Login') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderLoginModal',
      label: 'Login',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Login Modal FB Login') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderLoginModal',
      label: 'FBLogin',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Login Modal Sign Up') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderLoginModal',
      label: 'SignUp',
      action: 'click'
    }]);
  } else if (eventName == 'Service Order Deposit') {
    _t.push(['user_action', {
      section: 'ServiceDepositPage',
      label: 'OrderDepositPage',
      action: 'view'
    }]);
  } else if (eventName == 'Service Order Deposit Success') {
    _t.push(['user_action', {
      section: 'ServiceProjectPage',
      label: 'ServicePurchasedFromDeposit',
      action: 'view'
    }]);
  } else if (eventName == 'Dashboard MyProjects') {
    _t.push(['user_action', {
      section: 'Dashboard',
      subsection: 'Topnav',
      label: 'MyProjects'
    }]);
  } else if (eventName == 'Service Order Deposit Other Methods') {
    _t.push(['user_action', {
      section: 'ServiceViewPage',
      subsection: 'OrderDepositModal',
      label: 'OrderDepositOtherMethods',
      action: 'view'
    }]);
  } else if (eventName == 'Showcase Upsell PVP') {
    _ttref.push(['_clearEntrypoint', 'Project']);
    _ttref.push(['_setEntrypoint', 'Project_ShowcaseUpsell']);

    _t.push(['user_action', {
      section: 'ProjectShowcaseUpsell',
      subsection: 'RepostButton',
      label: event.source,
      action: 'click'
    }]);
  } else if (eventName == 'Front Page Log In') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'FrontPageUpperRight',
      action: 'click'
    }]);
  } else if (eventName == 'Post Project Log In') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'PostProjectPage',
      action: 'click'
    }]);
  } else if (eventName == 'Post Contest Log In') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'PostContestPage',
      action: 'click'
    }]);
  } else if (eventName == 'Top Right Corner') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'TopRightCorner',
      action: 'click'
    }]);
  } else if (eventName == 'Hire Me Page') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'HireMePage',
      action: 'click'
    }]);
  } else if (eventName == 'User Login Page') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'UserLogInPage',
      action: 'click'
    }]);
  } else if (eventName == 'Repost Page') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'RepostPage',
      action: 'click'
    }]);
  } else if (eventName == 'Sign Up Modal') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'SignUpModal',
      action: 'click'
    }]);
  } else if (eventName == 'Find Freelancers Page') {
    _t.push(['user_action', {
      section: 'Login',
      subsection: 'LoginAttempt',
      label: 'FindFreelancersPage',
      action: 'click'
    }]);
  } else if (eventName == 'Directory Page Search') {
    _t.push(['search', {
      section: 'Directory',
      source: event.source,
      keyword: event.keyword,
      filters: event.filters,
      items_displayed: event.items_displayed,
      items_count: event.items_count,
      items_page: event.items_page
    }]);
  } else if (eventName == 'Navigation Bar Search') {
    _t.push(['search', {
      section: 'MainNav',
      keyword: event.keyword
    }]);
  } else if (eventName == 'Jobs Page Search') {
    _t.push(['search', {
      section: 'JobTable',
      source: event.source,
      keyword: event.keyword,
      filters: event.filters,
      items_displayed: event.items_displayed,
      items_count: event.items_count,
      items_page: event.items_page,
      sort: event.sort
    }]);
  } else if (eventName == 'Work Search') {
    _t.push(['search', {
      section: 'WorkTable',
      source: event.source,
      keyword: event.keyword,
      filters: event.filters,
      items_displayed: event.items_displayed,
      items_count: event.items_count,
      items_page: event.items_page,
      sort: event.sort
    }]);
  } else if (eventName == 'Project View Page - Upsell Banner') {
    _t.push(['user_action', {
      section: 'ProjectViewPage',
      subsection: event.eventOption.subsection,
      label: event.eventOption.label,
      action: event.eventOption.action
    }]);
  } else if (eventName === 'Project View Page - Bid on this project') {
    _t.push(['user_action', {
      section: 'Project',
      sub_section: 'BidTheProject',
      label: event.label
    }]);
  } else {
    _t.push(event);
  }
}

function LocalTracking() {
  this._events = [];
}

LocalTracking.prototype._save = function () {
  try {
    localStorage.setItem('LocalTracking', JSON.stringify(this._events));
  } catch (e) {
    return false;
  }
};

LocalTracking.prototype._restore = function () {
  try {
    var events = JSON.parse(localStorage.getItem('LocalTracking'));
    for (var _i = 0, _len = events.length; _i < _len; _i++) {
      this.push(events[_i]);
    }
  } catch (e) {
    return false;
  }
};

LocalTracking.prototype._process = function () {
  var events = this._events;
  this._events = [];
  for (var _i = 0, _len = events.length; _i < _len; _i++) {
    processEvent(events[_i]);
  }
  try {
    localStorage.removeItem('LocalTracking');
  } catch (e) {
    return false;
  }
  return true;
};

LocalTracking.prototype.push = function (event, options) {
  var delay = 5000;

  if (typeof options === 'object') {
    delay = options.delay ? options.delay : delay;
  }

  this._events.push(event);
  this._save();
  var self = this;
  setTimeout(function () {
    self._process();
  }, delay);
  return 1;
};

LocalTracking.prototype.init = function (_t) {
  var event = void 0;
  var _i = void 0;
  var _len = void 0;
  this._restore();
  if (_t == null) {
    _t = [];
  }
  for (_i = 0, _len = _t.length; _i < _len; _i++) {
    event = _t[_i];
    this.push(event);
  }
  return this;
};

function lib_datetime_relative_date(time) {
  var now = new Date();
  var unixT = now.getTime();

  if (!time || !parseInt(time, 10) || parseInt(time, 10) < 0) {
    // Default to 'Just now' if not a valid timestamp
    return translate('Μόλις τώρα');
  }

  var diff = parseInt(unixT / 1000, 10) - parseInt(time, 10);
  if (diff >= 0) {
    if (diff < 30) {
      return translate('Μόλις τώρα');
    } else if (diff < 60) {
      return translate('πριν από <%- diff %> δευτερόλεπτα', {
        diff: diff });
    } else if (diff < 3600) {
      diff = parseInt(diff / 60, 10);
      if (diff == 1) return translate('1 λεπτό πριν');
      return translate('πριν από <%- diff %> λεπτά', {
        diff: diff });
    } else if (diff < 86400) {
      diff = parseInt(diff / 3600, 10);
      if (diff == 1) return translate('πειν από 1 ώρα');
      return translate('πριν από <%- diff %> ώρες', {
        diff: diff });
    } else if (diff < 86400 * 7) {
      diff = parseInt(diff / 86400, 10);
      if (diff == 1) return translate('Χθες');
      return translate('πριν από <%- diff %> μέρες', {
        diff: diff });
    } else if (diff < 86400 * 7 * 4.3) {
      diff = parseInt(diff / 86400 / 7, 10);
      if (diff == 1) return translate('Προηγούμενη εβδομάδα');
      return translate('πριν <%- diff %> εβδομάδες', {
        diff: diff });
    } else if (diff < 86400 * 365) {
      diff = parseInt(diff / 86400 / 30, 10);
      if (diff == 1) return translate('πριν 1 μήνα');
      return translate('πριν <%- diff %> μήνες', {
        diff: diff });
    } else if (diff < 86400 * 365 * 8) {
      diff = parseInt(diff / 86400 / 365, 10);
      if (diff == 1) return translate('πριν 1 χρόνο');
      return translate('πριν <%- diff %> χρόνια', {
        diff: diff });
    }
  } else {
    diff = Math.abs(diff);
    if (diff < 30) {
      return translate('Μόλις τώρα');
    } else if (diff < 60) {
      return translate('μένουν <%- diff %> δευτερόλεπτα', {
        diff: diff });
    } else if (diff < 3600) {
      diff = parseInt(diff / 60, 10);
      if (diff == 1) return translate('μένει 1 λεπτό');
      return translate('μένουν <%- diff %> λεπτά', {
        diff: diff });
    } else if (diff < 86400) {
      var minutes = diff % 3600;
      minutes = parseInt(minutes / 60, 10);
      diff = parseInt(diff / 3600, 10);
      if (diff == 1) {
        if (minutes == 1) {
          return translate('μένει 1 ώρα, 1 λεπτό');
        } else if (minutes == 0) {
          return translate('μένει 1 ώρα');
        }

        return translate('Μένουν 1 ώρα, <%- minutes %> λεπτά', {
          minutes: minutes });
      }

      if (minutes == 1) {
        return translate('Μένουν <%- diff %> ώρες, 1 λεπτό', {
          diff: diff });
      } else if (minutes == 0) {
        return translate('Μένουν <%- diff %> ώρες', {
          diff: diff });
      }

      return translate('Μένουν <%- diff %> ώρες, <%- minutes %> λεπτά', {
        diff: diff,
        minutes: minutes });
    } else if (diff < 86400 * 14) {
      // display days for 2 weeks
      var hours = diff % 86400;
      hours = parseInt(hours / 3600, 10);
      diff = parseInt(diff / 86400, 10);
      if (diff == 1) {
        if (hours == 1) {
          return translate('Μένει 1 μέρα, 1 ώρα');
        } else if (hours == 0) {
          return translate('Μένει 1 μέρα');
        }

        return translate('Μένει 1 μέρα, <%- hours %> ώρες', {
          hours: hours });
      }

      if (hours == 1) {
        return translate('Μένουν <%- diff %> μέρες, 1 ώρα', {
          diff: diff });
      } else if (hours == 0) {
        return translate('Μένουν <%- diff %> μέρες', {
          diff: diff });
      }

      return translate('Μένουν <%- diff %> μέρες, <%- hours %> ώρες', {
        diff: diff,
        hours: hours });
    }

    return translate('περισσότερο από 2 εβδομάδες');
  }
}

var commentTemplate = "<li class=\"discussion-thread-message\" data-comment=\"<%-  id  %>\" data-ts=\"<%-  timestamp  %>\">\n    <div class=\"article-comment\">\n        <div class=\"article-comment-avatar\">\n            <img class=\"article-comment-avatar-image\" src=\"<%=  img  %>\"\n                alt=\"<%-  public_name  %>\">\n        </div>\n        <div class=\"article-comment-body\">\n            <div class=\"article-comment-header\">\n                <p class=\"article-comment-name\">\n                    <a class=\"article-comment-link\" href=\"<%-  profile_url  %>\">\n                        <%-  public_name  %>\n                    </a>\n                    <%  if (staff) {  %>\n                        <span class=\"promotion-tag promotion-assisted\">\n                            <span class=\"fl-icon-bird\"></span><%- T_(\"Προσωπικό\") %>\n                        </span>\n                    <%  }  %>\n                </p>\n                <div class=\"article-comment-controls\">\n                    <span class=\"article-comment-date\">\n                        <time datetime=\"<%-  dateToIso(timestamp)  %>\">\n                            <%-  lib_datetime_relative_date(timestamp)  %>\n                        </time>\n                    </span>\n                    <span class=\"article-comment-social\">\n                        <a class=\"article-comment-link discussion-thread-reply-link\"\n                            data-replyto=\"<%-  id  %>\">\n                            <%- T_(\"Απάντηση\") %>\n                        </a>\n                        <%  if (can_be_hidden) {  %>\n                            <a class=\"article-comment-link discussion-thread-hide-link\"\n                                data-comment=\"<%-  id  %>\" data-hidden=\"<%-  hidden  %>\"\n                                data-article=\"<%-  article_id  %>\">\n                                <%  if (hidden == 0) {  %>\n                                    <%- T_(\"απόκρυψη\") %>\n                                <%  } else {  %>\n                                    <%- T_(\"εμφάνιση\") %>\n                                <%  }  %>\n                            </a>\n                        <%  }  %>\n                    </span>\n                </div>\n            </div>\n            <p class=\"article-comment-message\"><%-  content  %></p>\n        </div>\n    </div>\n    <ol class=\"article-discussion-board-thread-reply\"></ol>\n</li>\n";

var replyTemplate = "<li class=\"discussion-thread-message-reply\" data-comment=\"<%-  id  %>\" data-ts=\"<%-  timestamp  %>\">\n    <%  if (admin) {  %>\n        <button class=\"discussion-thread-message-remove-bt fl-icon-remove-circle\"></button>\n    <%  }  %>\n    <div class=\"article-comment\">\n        <div class=\"article-comment-avatar\">\n            <img class=\"article-comment-avatar-image\" src=\"<%=  img  %>\"\n                alt=\"<%-  public_name  %>\">\n        </div>\n        <div class=\"article-comment-body\">\n            <div class=\"article-comment-header\">\n                <p class=\"article-comment-name\">\n                    <a class=\"article-comment-link\" href=\"<%-  profile_url  %>\">\n                        <%-  public_name  %>\n                    </a>\n                    <%  if (staff) {  %>\n                        <span class=\"promotion-tag promotion-assisted\">\n                            <span class=\"fl-icon-bird\"></span><%- T_(\"Προσωπικό\") %>\n                        </span>\n                    <%  }  %>\n                </p>\n                <div class=\"article-comment-controls\">\n                    <span class=\"article-comment-date\">\n                        <time datetime=\"<%-  dateToIso(timestamp)  %>\">\n                            <%-  lib_datetime_relative_date(timestamp)  %>\n                        </time>\n                    </span>\n                    <span class=\"article-comment-social\">\n                        <a class=\"article-comment-link discussion-thread-reply-link\"\n                            data-replyto=\"<%-  parent_comment_id  %>\">\n                            <%- T_(\"Απάντηση\") %>\n                        </a>\n                        <%  if (can_be_hidden) {  %>\n                            <a class=\"article-comment-link discussion-thread-hide-link\"\n                                data-comment=\"<%-  id  %>\" data-hidden=\"<%-  hidden  %>\"\n                                data-article=\"<%-  article_id  %>\">\n                                <%  if (hidden == 0) {  %>\n                                    <%- T_(\"απόκρυψη\") %>\n                                <%  } else {  %>\n                                    <%- T_(\"εμφάνιση\") %>\n                                <%  }  %>\n                            </a>\n                        <%  }  %>\n                    </span>\n                </div>\n            </div>\n            <p class=\"article-comment-message\"><%-  content  %></p>\n        </div>\n    </div>\n</li>\n";

var commentInputTempate = "<li class=\"discussion-thread-message comment-reply comment-input\" data-comment=\"<%-  id  %>\">\n    <div class=\"article-comment\">\n        <div class=\"article-comment-avatar\">\n            <img class=\"article-comment-avatar-image\" src=\"<%=  user.img  %>\"\n                alt=\"<%-  user.public_name  %>\">\n        </div>\n        <div class=\"article-comment-body\">\n            <div class=\"article-comment-header\">\n                <p class=\"article-comment-name\">\n                    <a class=\"article-comment-link\" href=\"<%-  user.url  %>\">\n                        <%-  user.public_name  %>\n                    </a>\n                </p>\n            </div>\n            <textarea id=\"article-comment-inputReply\"\n                class=\"large-textarea article-comment-input\"\n                placeholder=\"<%- T_('Γράψτε εδώ το μήνυμά σας') %>\"\n                data-id=\"<%-  id  %>\"></textarea>\n            <button class=\"btn btn-info article-comment-btn\">Comment</button>\n        </div>\n    </div>\n</li>\n";

var showMoreTemplate = "<%  if (typeof num == \"undefined\") {  %>\n<a href=\"#\" class=\"discussion-thread-reveal-bt comment-show-more article-discussion-board-show-more\"><%- T_(\"Προβολή Περισσότερων\") %></a>\n<%  } else {  %>\n<button class=\"discussion-thread-reveal-bt reply-show-more\"><span class=\"discussion-thread-reveal-inner\" data-pid=\"<%-  pid  %>\"><%- T_('Δείτε <[= num ]> Προηγούμενα Μηνύματα', { 'num': num }) %></span></button>\n<%  }  %>\n";

var hiddenTemplate = "<li class=\"discussion-thread-message<%  if (reply) { %>-reply<%  }  %>\">\n    <span class=\"discussion-thread-message-removed\">\n        <%  if (reply) { %>\n        <%- T_(\"Αυτή η απάντηση έχει διαγραφεί\") %>\n        <%  } else { %>\n        <%- T_(\"Αυτό το σχόλιο έχει αφαιρεθεί\") %>\n        <%  }  %>\n    </span>\n</li>\n";

/**
 * contain the category logic for comments on article view page
 */

var admin = false;
var user = {};
var comment = {};
var comments = {};
var disableComment = false;

var commentTemplateCompiled = template(commentTemplate);
var replyTemplateCompiled = template(replyTemplate);
var showMoreTemplateCompiled = template(showMoreTemplate);
var hiddenTemplateCompiled = template(hiddenTemplate);
var commentInputTempateCompiled = template(commentInputTempate);

var storeUser = function storeUser() {
  user.img = jq('.article-comment-avatar.current-user img').attr('src');
  user.display_name = jq('.article-comment-avatar.current-user img').attr('alt');

  user.public_name = jq('.article-comment-avatar.current-user img').attr('alt');
  user.id = getUserIdfromCookie();
  user.profile_url = jq('#current-user-profile-url').attr('href');
  user.staff = false;
};

var postComment = function postComment(reply, event_source) {
  var input = getInputField(reply);
  sendCommentPostRequest({
    input: input,
    success: function success(resp) {
      if (resp.status == 'success') {
        comment = {
          article_id: jq('#discussion-board').data('article'),
          content: input.val(),
          timestamp: Math.round(new Date().getTime() / 1000),
          deleted: 0,
          id: resp.comment_id,
          img: user.img,
          parent_comment_id: '0',
          display_name: user.display_name,
          public_name: user.public_name,
          staff: user.staff,
          users_id: user.id,
          profile_url: user.profile_url,
          can_be_hidden: resp.can_be_hidden,
          hidden: 0 };

        if (reply) {
          comment.parent_comment_id = input.data('id');
          jq('.discussion-thread-message.comment-reply.comment-input').remove();
        }
        input.val('');
        renderOne(comment, true);
      } else if (resp.status === 'failed') {
        jq(event_source).after('<p class=\'article-comment-error\'> ' + resp.msg + ' </p>');
      }
      disableComment = false;
    },
    beforeSend: function beforeSend() {
      input.prop('disabled', true);
      jq('.article-comment-error').remove();
    },
    complete: function complete() {
      input.removeAttr('disabled');
    },
    error: function error() {
      jq('.form-error').text(translate('Εσωτερικό σφάλμα, παρακαλούμε δοκιμάστε ξανά αργότερα'));
    }
  });
};

var commentListener = function commentListener(el, event, reply) {
  var comment_help = jq(el).siblings('.comment-help');

  if (event.type == 'click') {
    if (jq(el).val().trim().length >= 5) {
      comment_help.text('');
      attempToPostComment(reply, event.target);
    } else if (jq(el).val().trim().length == 0) {
      comment_help.text(translate('Παρακαλούμε εισάγετε το σχόλιό σας'));
    } else {
      comment_help.text(translate('Παρακαλούμε εισάγετε τουλάχιστον 5 χαρακτήρες.'));
    }
  }
};

var dateToIso = function dateToIso(timestamp) {
  var date = new Date(1000 * parseInt(timestamp, 10));
  return date.toISOString();
};

function attempToPostComment(reply, event_source) {
  if (!getUserIdfromCookie()) {
    window.webappCompatModals.open('LoginSignupModal', {
      inputs: {
        form: 'login',
        loginHeader: translate('Κάντε είσοδο για να απαντήσετε σε αυτό το άρθρο'),
        skipSuccessRedirectView: true },

      size: 'xsmall',
      edgeToEdge: true,
      closeable: true,
      mobileFullscreen: true }, function (user) {
      if (user) {
        sendCommentPostRequest({
          input: getInputField(reply),
          success: function success() {
            window.location.reload();
          }
        });
      }
    });
  } else {
    postComment(reply, event_source);
  }
}

function sendCommentPostRequest(_ref) {
  var input = _ref.input,
      success = _ref.success,
      beforeSend = _ref.beforeSend,
      complete = _ref.complete,
      error = _ref.error;

  if (disableComment == false) {
    disableComment = true;
  } else {
    return;
  }
  var discussionBoard = jq('#discussion-board');
  jq.ajax({
    type: 'POST',
    url: '/ajax/articles/comment.php',
    cache: false,
    data: {
      id: discussionBoard.data('article'),
      parent_id: input.data('id'),
      comment: input.val(),
      csrf_token: getCSRFToken() },

    dataType: 'json',
    success: success,
    beforeSend: beforeSend,
    complete: complete,
    error: error });
}

function bindReplyAction() {
  var html = void 0;
  var textarea = void 0;

  jq('.discussion-thread-reply-link').unbind('click');
  jq('.discussion-thread-reply-link').click(function (e) {
    // If not login, popup login modal
    if (getUserIdfromCookie() === '') {
      e.preventDefault();
      window.webappCompatModals.open('LoginSignupModal', {
        inputs: {
          form: 'login',
          loginHeader: translate('Κάντε είσοδο για να απαντήσετε σε αυτό το άρθρο') },

        size: 'xsmall',
        edgeToEdge: true,
        closeable: true,
        mobileFullscreen: true }, function (user) {
        if (user) {
          window.location.reload();
        }
      });

      return;
    }

    // remove other reply inputs
    jq('.discussion-thread-message.comment-reply.comment-input').remove();

    // render reply input
    html = commentInputTempateCompiled({
      user: user,
      id: jq(e.target).data('replyto'),
      T_: translate,
      lib_datetime_relative_date: lib_datetime_relative_date,
      getUserIdfromCookie: getUserIdfromCookie });

    jq(e.target).parents('li.discussion-thread-message').after(html);

    // bind control + enter
    jq(e.target).parents('li.discussion-thread-message').next().find('textarea').focus().keydown(function (e) {
      commentListener(this, e, true);
    });
    // bind comment btn
    jq(e.target).parents('li.discussion-thread-message').next().find('.article-comment-btn').click(function (e) {
      textarea = jq(this).siblings('textarea');
      commentListener(textarea, e, true);
    });
  });
}

function bindHideComment() {
  jq('.discussion-thread-hide-link').off('click');
  jq('.discussion-thread-hide-link').click(function () {
    var el = this;

    jq.ajax({
      type: 'POST',
      url: '/ajax/articles/hideComment.php',
      data: {
        csrf_token: getCSRFToken(),
        id: jq(el).data('comment'),
        article_id: jq(el).data('article'),
        hidden: jq(el).data('hidden') },

      dataType: 'json',
      success: function success(resp) {
        if (resp.status === 'success') {
          jq(el).data('hidden', resp.state);
          jq(el).closest('li[class^=discussion-thread-message]').toggleClass('hidden', resp.state === 1);
          jq(el).text(function () {
            if (resp.state === 1) {
              return 'unhide';
            }
            return 'hide';
          });
        }
      }
    });
  });
}

function renderOne(comment, newlyPosted) {
  var data = void 0;
  var templateComment = void 0;
  var html = void 0;

  if (typeof newlyPosted === 'undefined') {
    newlyPosted = false;
  }
  var isReply = comment.parent_comment_id != 0;

  // choose template
  if (isReply) {
    templateComment = replyTemplateCompiled;
  } else {
    templateComment = commentTemplateCompiled;
  }

  if (comment.deleted == 1) {
    templateComment = hiddenTemplateCompiled;
  }

  // prepare data
  data = {
    admin: admin,
    reply: isReply,
    user: user,
    can_hide_comments: false,
    T_: translate,
    lib_datetime_relative_date: lib_datetime_relative_date,
    getUserIdfromCookie: getUserIdfromCookie,
    dateToIso: dateToIso };

  data = jq.extend({}, data, comment);

  // insert html
  html = templateComment(data);

  // Check if hidden
  if (window.parseInt(data.hidden) === 1) {
    html = jq(html).addClass('hidden');
  }

  if (newlyPosted) {
    if (isReply) {
      jq('.discussion-thread-message[data-comment=\n        ' + comment.parent_comment_id + ']').children('.article-discussion-board-thread-reply').append(html);
    } else {
      jq('.article-discussion-board-thread').prepend(html);
    }
  } else if (isReply) {
    jq('.discussion-thread-message[data-comment=\n        ' + comment.parent_comment_id + ']').children('.article-discussion-board-thread-reply').prepend(html);
  } else {
    jq('.article-discussion-board-thread').append(html);
  }

  // bind action
  if (!admin) {
    bindReplyAction(comment);
  }

  if (data.can_be_hidden) {
    bindHideComment();
  }
}

function renderComment(comments, count, pid) {
  if (pid == 0) {
    jq.each(comments, function (i, comment) {
      renderOne(comment);
      if (comment.reply) {
        jq.each(comment.reply, function (k, reply) {
          renderOne(reply);
        });
        // initial see more for comment replies
        initSeemore(comment.count, comment.id);
      }
    });
    // initial see more for comments
    initSeemore(count, 0);
  } else {
    jq.each(comments, function (i, comment) {
      renderOne(comment);
    });
    initSeemore(count, pid);
  }

  if (admin) {
    bindDeleteComment();
  }
}

// fetch comments based on start timestamp and parent comment id
function fetchComments(start, pid) {
  var parent_comment = jq('.article-discussion-board');
  var discussionBoard = jq('.article-discussion-board');

  if (!pid) {
    pid = 0;
  }

  jq.ajax({
    type: 'GET',
    url: '/ajax/articles/getComments.php',
    cache: false,
    data: {
      id: discussionBoard.data('article'),
      start: start,
      pid: pid },

    dataType: 'json',
    success: function success(resp) {
      if (resp.status == 'success') {
        if (pid == 0) {
          comments = jq.extend([], comments, resp.comments);
        } else {
          parent_comment = comments.find(function (comment) {
            return comment.id == pid;
          });
          parent_comment.reply = jq.extend([], parent_comment.reply, resp.comments);
        }
        renderComment(resp.comments, resp.count, pid);
      }
    }
  });
}

function initSeemore(count, pid) {
  var _this = this;

  var first = void 0;
  var replies = void 0;
  var seemore = void 0;

  if (count > 20) {
    if (pid == 0) {
      seemore = showMoreTemplateCompiled({ T_: translate });
      jq('.article-discussion-board-thread').after(seemore);
      jq('.comment-show-more').click(function (e) {
        e.preventDefault();
        jq('.comment-show-more').remove();
        first = Math.min.apply(0, comments.map(function (comment) {
          return comment.timestamp;
        }));
        fetchComments(first);
      });
    } else {
      seemore = showMoreTemplateCompiled({ num: count - 20, pid: pid, T_: translate });
      jq('.discussion-thread-message[data-comment= ' + pid + ']\n        .article-discussion-board-thread-reply').before(seemore);
      jq('.reply-show-more span').click(function () {
        jq(_this).closest('.reply-show-more').remove();
        replies = comments.find(function (comment) {
          return comment.id == pid;
        }).reply;
        first = Math.min.apply(0, replies.map(function (reply) {
          return reply.timestamp;
        }));
        fetchComments(first, pid);
      });
    }
  }
}

// admin user only
function bindDeleteComment() {
  jq('.discussion-thread-message-remove-bt').unbind('click');
  jq('.discussion-thread-message-remove-bt').click(function (e) {
    var el = this;

    jq.ajax({
      type: 'POST',
      url: '/admin/ajax/articles/deleteComment.php',
      cache: false,
      data: {
        id: jq(e.target).closest('li').data('comment') },

      dataType: 'json',
      success: function success(resp) {
        if (resp.status == 'success') {
          jq(el).parent('li').replaceWith(hiddenTemplateCompiled({
            T_: translate,
            reply: jq(e.target).closest('li').hasClass('discussion-thread-message.comment-reply') }));
        }
      }
    });
  });
}

function setTimeText() {
  var ts = void 0;
  var text = void 0;

  jq.each(jq('.article-discussion-thread-message'), function (i, el) {
    ts = jq(el).data('ts');
    if (ts) {
      text = lib_datetime_relative_date(ts);
      jq('.discussion-thread-message-date time', jq(el)).text(text);
    }
  });

  jq.each(jq('.discussion-thread-message.comment-reply'), function (i, el) {
    ts = jq(el).data('ts');
    if (ts) {
      text = lib_datetime_relative_date(ts);
      jq('.discussion-thread-message-date time', jq(el)).text(text);
    }
  });
  // start 15 sec later
  setTimeout(function () {
    setTimeText();
  }, 15000);
}

function bindActions() {
  var textarea = void 0;
  // bind control + enter
  jq('#article-comment-inputReply, #article-comment-input').keydown(function (e) {
    commentListener(this, e, false);
  });

  // bind comment btn
  jq('.article-comment-btn').click(function (e) {
    textarea = jq(this).siblings('textarea');
    commentListener(textarea, e, false);
  });
}

function init(isAdmin) {
  if (isAdmin) {
    admin = isAdmin;
  }
  fetchComments();
  bindActions();
  storeUser();
  setTimeout(function () {
    setTimeText();
  }, 5000);
}

function getInputField(isReply) {
  if (isReply) {
    return jq('#article-comment-inputReply');
  }
  return jq('#article-comment-input');
}

var articleComments = {
  init: init };

/**
 * Like Modal on article view page
 */

var lt = new LocalTracking();
var likeModalEl = jq('#likeModal');
var likeModalWrapper = jq('#likeModalWrapper');

function init$1() {
  if (likeModalEl.length > 0) {
    likeModalWrapper.hide();
  }
}

function openModal() {
  likeModalWrapper.show();
  jq('body').addClass('modal-open');
  likeModalWrapper.removeClass('LikeModalView');
  likeModalEl.modal();
  pushData(likeModalEl.data('label'), likeModalEl.data('articleid'));

  likeModalEl.find('#close').on('click', function () {
    pushData(jq(this).data('label'), likeModalEl.data('articleid'));
    likeModalWrapper.hide();
    jq('body').removeClass('modal-open');
    likeModalWrapper.addClass('LikeModalView');
  });

  likeModalEl.find('#hireMeBtn, a[data-label="GoToProfile"]').on('click', function () {
    pushData(jq(this).data('label'), likeModalEl.data('articleid'));
  });
}

function pushData(label, article_id) {
  lt.push(['user_action', {
    section: 'Community',
    subsection: 'HireMeModal',
    label: label,
    reference: 'article_id',
    reference_id: article_id
  }]);
}

var likeModal = {
  init: init$1,
  openModal: openModal
};

/**
 * Contains the logic for the article view page
 */

var lt$1 = new LocalTracking();

jq('#article-comment-login').click(function (e) {
  e.preventDefault();
  window.webappCompatModals.open('LoginSignupModal', {
    inputs: {
      form: 'login',
      loginHeader: translate('Κάντε είσοδο για να σχολιάσετε αυτό το άρθρο') },

    size: 'xsmall',
    edgeToEdge: true,
    closeable: true,
    mobileFullscreen: true }, function (user) {
    if (user) {
      window.location.reload();
    }
  });
});

function initTop() {
  jq('.backtotop').click(function () {
    jq('html, body').animate({ scrollTop: 0 }, 1000);
  });
}

function initVote() {
  jq('.btn-rating').click(function (e) {
    e.preventDefault();
    if (getUserIdfromCookie() === '') {
      window.webappCompatModals.open('LoginSignupModal', {
        inputs: {
          form: 'login',
          loginHeader: translate('Κάντε είσοδο για να υπερψηφίσετε αυτό το άρθρο') },

        size: 'xsmall',
        edgeToEdge: true,
        closeable: true,
        mobileFullscreen: true }, function (user) {
        if (user) {
          window.location.reload();
        }
      });
    }

    var voteBtn = jq('.btn-rating');
    var hasVotedUp = false;
    jq.each(voteBtn, function (i, btn) {
      if (jq(btn).hasClass('is-active')) {
        hasVotedUp = true;
      }
    });

    // toggles current vote of user
    var changedVote = hasVotedUp ? 0 : 1;
    jq.ajax({
      type: 'POST',
      url: '/ajax/articles/vote.php',
      cache: false,
      data: {
        id: jq(e.target).data('id'),
        like: changedVote,
        csrf_token: getCSRFToken() },

      dataType: 'json',
      beforeSend: function beforeSend() {
        if (changedVote === 1) {
          voteBtn.addClass('is-active');
        } else {
          voteBtn.removeClass('is-active');
        }
      },
      success: function success(resp) {
        if (resp.status === 'success') {
          jq('.vote-count').text(resp.votes);

          // hireme modal
          if (changedVote === 1 && jq('#likeModal').length > 0) {
            likeModal.openModal();
          }
        } else if (resp.status === 'failed') {
          if (changedVote === 1) {
            voteBtn.removeClass('is-active');
          } else {
            voteBtn.addClass('is-active');
          }
        }
      }
    });
  });
}

function initUnpublish() {
  jq('.btn-unpublish').click(function (e) {
    jq.ajax({
      type: 'POST',
      url: '/ajax/articles/unpublish.php',
      cache: false,
      data: {
        id: jq(e.target).data('id'),
        csrf_token: getCSRFToken() },

      dataType: 'json',
      beforeSend: function beforeSend() {
        jq(e.target).addClass('disabled');
      },
      success: function success(resp) {
        if (resp.status !== 'success') {
          jq(e.target).removeClass('disabled');
        } else {
          window.location.href = resp.redirect;
        }
      }
    });
  });
}

function initEdit() {
  jq('.btn-article-edit').click(function (e) {
    window.location.href = '/community/edit-article/' + jq(e.target).data('id');
  });
}

function initReview() {
  jq('.btn-review').click(function (e) {
    window.location.href = '/admin/articles/view.php?edit=1&id=' + jq(e.target).data('id');
  });
}

function initHireme() {
  // Register online offline for freelancers.
  OnlineOffline.sub([jq('.online-status').data('userid')]);
}

function commentValueLookup() {
  jq(document).on('keyup', '#article-comment-inputReply, #article-comment-input', function () {
    var valLength = jq(this).val().length;
    var btn = jq(this).parent().find('.article-comment-btn');
    if (valLength > 5) {
      if (!jq(this).hasClass('is-ok')) {
        btn.fadeIn('linear');
        jq(this).addClass('is-ok');
      }
    } else {
      btn.fadeOut('linear');
      jq(this).removeClass('is-ok');
    }
  });
}

function initDiscussionBoard() {
  if (window.location.hash) {
    var hashValue = window.location.hash.replace('/', '');
    if (hashValue === '#comments') {
      jq('html, body').animate({
        scrollTop: jq('#discussion-board').offset().top - 50 }, 1000);
    }
  }
}

function initSidebar() {
  jq('#article-top #side-bar #hireme-sidebar').click(function () {
    var elem = jq(this);
    pushData$1('ArticleStickySidebar', 'GoToProfileHireme', 'user_id', elem.data('userid'), elem.data('articleid'), 'article_id');
  });

  jq('#article-top #side-bar #profile-sidebar').click(function () {
    var elem = jq(this);
    pushData$1('ArticleStickySidebar', 'GoToProfile', 'user_id', elem.data('userid'), elem.data('articleid'), 'article_id');
  });

  var shareBtn = jq('#article-top #side-bar');
  shareBtn.find('#share-fb-sidebar, #share-tw-sidebar, #share-in-sidebar').click(function () {
    var elem = jq(this);
    pushData$1('ArticleStickySidebar', 'ShareArticle', 'article_id', elem.data('articleid'), elem.data('channel'));
  });
}

function initFooter() {
  jq('#hireme-footer').click(function () {
    var elem = jq(this);
    pushData$1('ArticleFooter', 'GoToProfileHireme', 'user_id', elem.data('userid'), elem.data('articleid'), 'article_id');
  });

  jq('#profile-footer').click(function () {
    var elem = jq(this);
    pushData$1('ArticleFooter', 'GoToProfile', 'user_id', elem.data('userid'), elem.data('articleid'), 'article_id');
  });

  var shareBtn = jq('#user-footer');
  shareBtn.find('#share-fb-footer, #share-tw-footer, #share-in-footer').click(function () {
    var elem = jq(this);
    pushData$1('ArticleFooter', 'ShareArticle', 'article_id', elem.data('articleid'), elem.data('channel'));
  });
}

function initCommentLoginBtn() {
  var elem = jq('#discussion-board #article-comment-login');
  if (elem.length) {
    elem.click(function () {
      pushData$1('ArticleComments', 'LoginToComment', 'article_id', jq(this).data('articleid'));
    });
  }
}

function pushData$1(subsection, label, reference, reference_id, value, name) {
  lt$1.push(['user_action', {
    section: 'Community',
    subsection: subsection,
    label: label,
    reference: reference,
    reference_id: reference_id,
    name: name !== undefined ? name : null,
    value: value !== undefined ? value : null }]);
}

function init$2() {
  initVote();
  initTop();
  initHireme();
  articleComments.init(false);
  initEdit();
  initReview();
  initUnpublish();
  commentValueLookup();
  initDiscussionBoard();
  initSidebar();
  initFooter();
  initCommentLoginBtn();
  likeModal.init();

  if (window.performance && window.performance.mark) {
    window.performance.mark('article_view_ready');
  }
}

var communityArticle = {
  init: init$2 };

communityArticle.init();
